import { Button } from '@sjuni/ui';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';

// Re-exported so a module importing this screen imports one thing rather than
// reaching past it into the table it happens to be built on.
export type { Column };
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useListParams } from '@/lib/list/useListParams';

/**
 * The chrome §4.6 puts around every list, once.
 *
 * "Every list has: search, relevant filters, column sorting, per-page selector,
 * bulk selection with bulk actions, a 'show deleted' toggle with restore,
 * sticky header, keyboard row navigation, and skeleton loading."
 *
 * The news and documents lists are hand-built because each has something the
 * others do not — a cover thumb, an upload dialog, a delete that has to check
 * its references first. The five simple content modules have none of that: a
 * title, a status, a date, and the same delete confirmation. This is that
 * screen, so those five are a column list rather than five copies of two
 * hundred lines.
 *
 * The restore and delete *actions* are here too rather than in each module's
 * columns. They were the part most likely to drift — one module forgetting the
 * permission check, another forgetting that a trashed row shows Restore instead
 * of Delete.
 */
export interface ResourceListScreenProps<TRow extends { id: number; deleted_at: string | null }> {
  /** The `/admin/{resource}` segment, and the `/{resource}` route. */
  resource: string;
  /** §3's `{resource}` half — usually the same word, but `news` is not `tags`. */
  permission?: string;
  title: string;
  intro: string;
  /** Without the actions column; this adds it. */
  columns: Column<TRow>[];
  /** How a row names itself in the delete confirmation. */
  labelFor: (row: TRow) => string;
  defaultSort?: string;
  filters?: React.ReactNode;
  emptyTitle: string;
  emptyBody: string;
  newLabel: string;
  /**
   * Sits beside "New …" — the staff importer, and whatever else a module has
   * that acts on the list rather than on one row.
   */
  secondaryAction?: React.ReactNode;
  deleteTitle: string;
  /** Given the row's label. */
  deleteBody: (label: string) => string;
  /**
   * What opening a row does. Defaults to navigating to `/{resource}/{id}`.
   *
   * §5.7's inbox overrides it for a drawer: the thing staff do there is triage
   * — open, read, mark, next — and a page per message makes that four
   * navigations each.
   */
  onOpen?: (row: TRow) => void;
}

export function ResourceListScreen<TRow extends { id: number; deleted_at: string | null }>({
  resource,
  permission,
  title,
  intro,
  columns,
  labelFor,
  defaultSort,
  filters,
  emptyTitle,
  emptyBody,
  newLabel,
  secondaryAction,
  deleteTitle,
  deleteBody,
  onOpen,
}: ResourceListScreenProps<TRow>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();
  const list = useListParams(defaultSort);

  const [deleting, setDeleting] = useState<TRow | null>(null);

  const { data, isPending, isError } = useResourceList<TRow>(resource, list.params);
  const act = useResourceAction<TRow>(resource);

  const gate = permission ?? resource;
  const mayCreate = can(`${gate}.create`);
  const mayDelete = can(`${gate}.delete`);

  const withActions: Column<TRow>[] = [
    ...columns,
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) => {
        if (row.deleted_at) {
          return (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                act.mutate({ id: row.id, action: 'restore' });
              }}
            >
              {t('list.restore')}
            </Button>
          );
        }

        // Absent rather than present-and-refused for a role §3 withholds it
        // from — a button that always 403s teaches an editor to distrust them.
        return mayDelete ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
            onClick={(event) => {
              event.stopPropagation();
              setDeleting(row);
            }}
          >
            {t('common.delete')}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{title}</h1>
        <p className="mt-1 text-body-sm text-secondary">{intro}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        filters={filters}
        /*
         * The two are gated separately. They were nested, which meant a module
         * whose role holds no `create` — §5.7's inbox, where nobody writes a
         * message — lost its secondary action along with the button it was
         * sitting beside. Whether a screen has a CSV export has nothing to do
         * with whether the reader may add a row.
         */
        primaryAction={
          mayCreate || secondaryAction ? (
            <div className="flex flex-wrap items-center gap-2">
              {secondaryAction}

              {mayCreate ? (
                <Button
                  type="button"
                  size="sm"
                  iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
                  onClick={() => navigate(`/${resource}/new`)}
                >
                  {newLabel}
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={withActions}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={onOpen ?? ((row) => navigate(`/${resource}/${row.id}`))}
        emptyTitle={emptyTitle}
        emptyBody={emptyBody}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={deleteTitle}
        body={deleteBody(deleting ? labelFor(deleting) : '')}
        confirmLabel={t('common.delete')}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />
    </div>
  );
}
